// Association Card Component
// Custom web component for displaying association/partnership cards

interface AssociationConfig {
  name: string;
  logo?: string;
  url: string;
  description: {
    en: string;
    lv: string;
    it: string;
  };
}

class AssociationCard extends HTMLElement {
  private config: AssociationConfig | null = null;

  static get observedAttributes() {
    return ['name', 'logo', 'url', 'description-en', 'description-lv', 'description-it'];
  }

  connectedCallback() {
    this.loadConfigFromAttributes();
    this.render();
  }

  attributeChangedCallback() {
    this.loadConfigFromAttributes();
    this.render();
  }

  private loadConfigFromAttributes() {
    const name = this.getAttribute('name');
    const url = this.getAttribute('url');
    const descEn = this.getAttribute('description-en');
    const descLv = this.getAttribute('description-lv');
    const descIt = this.getAttribute('description-it');

    if (name && url && descEn) {
      this.config = {
        name,
        logo: this.getAttribute('logo') || undefined,
        url,
        description: {
          en: descEn,
          lv: descLv || descEn,
          it: descIt || descEn,
        },
      };
    }
  }

  setConfig(config: AssociationConfig) {
    this.config = config;
    this.render();
  }

  private render() {
    if (!this.config) return;

    const logoHtml = this.config.logo
      ? `<img src="${this.config.logo}" alt="${this.config.name}" class="h-12 w-auto object-contain">`
      : `<span class="text-indigo-600 font-bold text-lg">${this.config.name.charAt(0)}</span>`;

    this.innerHTML = `
      <a href="${this.config.url}" target="_blank" rel="noopener noreferrer"
         class="flex flex-col items-center p-6 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group">
        <div class="mb-4 h-16 w-16 bg-indigo-400 rounded-xl flex items-center justify-center grayscale group-hover:grayscale-0 transition-all">
          ${logoHtml}
        </div>
        <h3 class="text-lg font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
          ${this.config.name}
        </h3>
        <p class="text-sm text-slate-600 text-center leading-relaxed"
           data-en="${this.config.description.en}"
           data-lv="${this.config.description.lv}"
           data-it="${this.config.description.it}">
          ${this.config.description.en}
        </p>
        <div class="mt-4 flex items-center gap-1 text-indigo-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <span data-en="Learn more" data-lv="Uzzināt vairāk" data-it="Scopri di più">Learn more</span>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
          </svg>
        </div>
      </a>
    `;

    // Re-apply language after rendering
    if (globalThis.switchLang) {
      const currentLang = localStorage.getItem('dammi_lang') || 'en';
      globalThis.switchLang(currentLang);
    }
  }
}

customElements.define('association-card', AssociationCard);

export { AssociationCard, type AssociationConfig };

